// TemplateSetupPanel.tsx
import React, { useState, useRef, useEffect, useCallback } from "react";
import apiClient from "../../stateManagers/services/axiosInterceptor";
import { PAGE_W, PAGE_H } from "./letterConstants";
import { useSchoolDetailsStore } from "../../stateManagers/newStores/useSchoolDetailsStore";
import { useAuthStore } from "../../stateManagers/store/authStore";

interface OptionResponse {
  status: string;
  data: {
    [key: string]: any;
  };
}

interface OptionFileAttachment {
  url: string;
  path: string;
  [key: string]: any;
}

interface OptionFilesResponse {
  status: string;
  message?: string;
  files?: OptionFileAttachment[];
}

// ✅ FIX: Pass schoolId to getOptions
async function getOptions(names: string[], schoolId?: string): Promise<Record<string, any>> {
  const params: any = { names: names.join(",") };
  if (schoolId) {
    params.school_id = schoolId;
  }
  const response = await apiClient.get<OptionResponse>("/api/user-options/multiple", { params });
  if (response.data.status === "success") {
    return response.data.data;
  }
  return {};
}

// ✅ FIX: Pass schoolId to upsertOption
async function upsertOption(name: string, value: any, schoolId?: string): Promise<void> {
  const payload: any = {
    option_name: name,
    option_value: value,
    autoload: "yes"
  };
  if (schoolId) {
    payload.school_id = schoolId;
  }
  const response = await apiClient.post("/api/user-options", payload);
  if (response.data.status !== "success" && response.data.status !== "created") {
    throw new Error(response.data.message || `Failed to save option "${name}"`);
  }
}

// ── Option Files (header/footer image uploads) ─────────────────────────────

async function getOptionFiles(name: string, schoolId?: string): Promise<OptionFileAttachment[]> {
  try {
    const params: any = {};
    if (schoolId) params.school_id = schoolId;
    const response = await apiClient.get<OptionFilesResponse>(`/api/options-files/${name}`, { params });
    if (response.data.status === "success") {
      return response.data.files || [];
    }
  } catch {
    // 404 (not uploaded yet) or any fetch error — treat as no file
  }
  return [];
}

async function saveOptionFile(name: string, file: File, schoolId?: string): Promise<OptionFileAttachment[]> {
  const formData = new FormData();
  formData.append("files", file);
  formData.append("autoload", "yes");
  formData.append("existing_files", "none");
  if (schoolId) {
    formData.append("school_id", schoolId);
    console.log('🔵 saveOptionFile - Adding school_id to formData:', schoolId);
  } else {
    console.warn('🔴 saveOptionFile - No school_id available!');
  }

  const response = await apiClient.put<OptionFilesResponse>(`/api/options-files/${name}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  if (response.data.status === "error") {
    throw new Error(response.data.message || `Failed to save file for "${name}"`);
  }
  return response.data.files || [];
}

async function deleteOptionFile(name: string, schoolId?: string): Promise<void> {
  const params: any = {};
  if (schoolId) params.school_id = schoolId;
  const response = await apiClient.delete<OptionFilesResponse>(`/api/options-files/${name}`, { params });
  if (response.data.status === "error") {
    throw new Error(response.data.message || `Failed to remove file for "${name}"`);
  }
}

export interface TemplateBlock {
  x: number;
  y: number;
  width: number;
}

export interface TemplateConfig {
  serial: TemplateBlock;
  date: TemplateBlock;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

const DEFAULTS: TemplateConfig = {
  serial: { x: 80,  y: 195, width: 220 },
  date:   { x: 520, y: 195, width: 180 },
};

const PANEL_CANVAS_W = 372;
const SCALE = PANEL_CANVAS_W / PAGE_W;
const PANEL_CANVAS_H = Math.round(PAGE_H * SCALE);

type BlockKey = "serial" | "date";

interface DragState {
  key: BlockKey;
  startMouseX: number;
  startMouseY: number;
  startElX: number;
  startElY: number;
}

const BLOCK_META: { key: BlockKey; label: string; icon: string }[] = [
  { key: "serial", label: "S. No. :-", icon: "pi-hashtag" },
  { key: "date",   label: "Date :-",   icon: "pi-calendar" },
];

const BLOCK_STYLES: Record<BlockKey, { bg: string; border: string; text: string }> = {
  serial: { bg: "rgba(40,85,174,0.10)", border: "#2855ae", text: "#1e3a8a" },
  date:   { bg: "rgba(40,85,174,0.06)", border: "#3b82f6", text: "#1e40af" },
};

export default function TemplateSetupPanel({ open, onClose }: Props) {
  const [config, setConfig]               = useState<TemplateConfig>(DEFAULTS);
  const [headerFile, setHeaderFile]       = useState<File | null>(null);
  const [footerFile, setFooterFile]       = useState<File | null>(null);
  const [headerLocalPreview, setHeaderLocalPreview] = useState<string | null>(null);
  const [footerLocalPreview, setFooterLocalPreview] = useState<string | null>(null);
  const [saving, setSaving]               = useState(false);
  const [loading, setLoading]             = useState(false);
  const [activeBlock, setActiveBlock]     = useState<BlockKey | null>(null);
  const [toast, setToast]                 = useState<{ msg: string; ok: boolean } | null>(null);
  const [previewHeader, setPreviewHeader] = useState<string | null>(null);
  const [previewFooter, setPreviewFooter] = useState<string | null>(null);

  const dragRef        = useRef<DragState | null>(null);
  const toastTimer     = useRef<ReturnType<typeof setTimeout> | null>(null);
  const headerInputRef = useRef<HTMLInputElement | null>(null);
  const footerInputRef = useRef<HTMLInputElement | null>(null);

  // ── Get school details from store ─────────────────────────────────────────
  const { schoolDetails, fetchSchoolDetails } = useSchoolDetailsStore();
  
  // ✅ Get school_id from schoolDetails
  const schoolId = schoolDetails?.school_id || "";
  const schoolIdCode = schoolDetails?.school_id_code || "";

  // ── Fetch school details on mount ──────────────────────────────────────────
  useEffect(() => {
    const storedSchoolId = localStorage.getItem('schoolId') || '';
    if (storedSchoolId && !schoolDetails) {
      fetchSchoolDetails(storedSchoolId);
    }
  }, [schoolDetails, fetchSchoolDetails]);

  // ✅ Debug: Log schoolId when it changes
  useEffect(() => {
    console.log('🔵 schoolId from schoolDetails:', schoolId);
    console.log('🔵 schoolIdCode from schoolDetails:', schoolIdCode);
  }, [schoolId, schoolIdCode]);

  const showToast = useCallback((msg: string, ok = true) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ msg, ok });
    toastTimer.current = setTimeout(() => setToast(null), 2800);
  }, []);

  // ── local object-URL previews ──────────────────────────────────────────────
  useEffect(() => {
    if (!headerFile) { setHeaderLocalPreview(null); return; }
    const url = URL.createObjectURL(headerFile);
    setHeaderLocalPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [headerFile]);

  useEffect(() => {
    if (!footerFile) { setFooterLocalPreview(null); return; }
    const url = URL.createObjectURL(footerFile);
    setFooterLocalPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [footerFile]);

  // ── fetch on open ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setHeaderFile(null);
    setFooterFile(null);

    console.log('🔵 Fetching options with schoolId:', schoolId);

    Promise.all([
      // ✅ Pass schoolId to getOptions
      getOptions(["letterhead_serial", "letterhead_date"], schoolId),
      getOptionFiles("letterhead_header_image", schoolId),
      getOptionFiles("letterhead_footer_image", schoolId),
    ])
      .then(([opts, headerFiles, footerFiles]) => {
        const serial = opts.letterhead_serial;
        const date   = opts.letterhead_date;

        setConfig({
          serial: serial ? JSON.parse(serial) : DEFAULTS.serial,
          date:   date   ? JSON.parse(date)   : DEFAULTS.date,
        });
        setPreviewHeader(headerFiles[0]?.url || null);
        setPreviewFooter(footerFiles[0]?.url || null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, schoolId]);

  // ── mouse drag ────────────────────────────────────────────────────────────
  const onMouseDown = (e: React.MouseEvent, key: BlockKey) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveBlock(key);
    dragRef.current = {
      key,
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      startElX: config[key].x,
      startElY: config[key].y,
    };
  };

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragRef.current) return;
      const { key, startMouseX, startMouseY, startElX, startElY } = dragRef.current;
      const dx = (e.clientX - startMouseX) / SCALE;
      const dy = (e.clientY - startMouseY) / SCALE;
      const blk = config[key];
      const newX = Math.max(0, Math.min(PAGE_W - blk.width, Math.round(startElX + dx)));
      const newY = Math.max(0, Math.min(PAGE_H - 40, Math.round(startElY + dy)));
      setConfig((p) => ({ ...p, [key]: { ...p[key], x: newX, y: newY } }));
    };
    const onUp = () => { dragRef.current = null; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup",   onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup",   onUp);
    };
  }, [config]);

  // ── touch drag ────────────────────────────────────────────────────────────
  const onTouchStart = (e: React.TouchEvent, key: BlockKey) => {
    e.stopPropagation();
    const t = e.touches[0];
    setActiveBlock(key);
    dragRef.current = {
      key,
      startMouseX: t.clientX,
      startMouseY: t.clientY,
      startElX: config[key].x,
      startElY: config[key].y,
    };
  };

  useEffect(() => {
    const onMove = (e: TouchEvent) => {
      if (!dragRef.current) return;
      const t = e.touches[0];
      const { key, startMouseX, startMouseY, startElX, startElY } = dragRef.current;
      const dx = (t.clientX - startMouseX) / SCALE;
      const dy = (t.clientY - startMouseY) / SCALE;
      const blk = config[key];
      const newX = Math.max(0, Math.min(PAGE_W - blk.width, Math.round(startElX + dx)));
      const newY = Math.max(0, Math.min(PAGE_H - 40, Math.round(startElY + dy)));
      setConfig((p) => ({ ...p, [key]: { ...p[key], x: newX, y: newY } }));
    };
    const onUp = () => { dragRef.current = null; };
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend",  onUp);
    return () => {
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend",  onUp);
    };
  }, [config]);

  // ── nudge inputs ──────────────────────────────────────────────────────────
  const nudge = (key: BlockKey, field: "x" | "y" | "width", val: number) =>
    setConfig((p) => ({ ...p, [key]: { ...p[key], [field]: Math.max(0, val) } }));

  // ── header/footer image handlers ────────────────────────────────────────
  const onHeaderFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) setHeaderFile(f);
    e.target.value = "";
  };

  const onFooterFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) setFooterFile(f);
    e.target.value = "";
  };

  const handleRemoveHeader = async () => {
    if (headerFile) {
      setHeaderFile(null);
      return;
    }
    if (!previewHeader) return;
    try {
      await deleteOptionFile("letterhead_header_image", schoolId);
      setPreviewHeader(null);
      showToast("Header image removed", true);
    } catch (err: any) {
      showToast(err.message || "Failed to remove header image", false);
    }
  };

  const handleRemoveFooter = async () => {
    if (footerFile) {
      setFooterFile(null);
      return;
    }
    if (!previewFooter) return;
    try {
      await deleteOptionFile("letterhead_footer_image", schoolId);
      setPreviewFooter(null);
      showToast("Footer image removed", true);
    } catch (err: any) {
      showToast(err.message || "Failed to remove footer image", false);
    }
  };

  // ── save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    console.log('🔵 Saving with schoolId:', schoolId);
    console.log('🔵 Saving with schoolIdCode:', schoolIdCode);
    console.log('🔵 Header file:', headerFile ? headerFile.name : 'None');
    console.log('🔵 Footer file:', footerFile ? footerFile.name : 'None');
    
    try {
      const tasks: Promise<any>[] = [
        // ✅ Pass schoolId to upsertOption
        upsertOption("letterhead_serial", JSON.stringify(config.serial), schoolId),
        upsertOption("letterhead_date", JSON.stringify(config.date), schoolId),
      ];

      if (headerFile) {
        console.log('🔵 Saving header file with schoolId:', schoolId);
        tasks.push(saveOptionFile("letterhead_header_image", headerFile, schoolId));
      }
      if (footerFile) {
        console.log('🔵 Saving footer file with schoolId:', schoolId);
        tasks.push(saveOptionFile("letterhead_footer_image", footerFile, schoolId));
      }

      const results = await Promise.all(tasks);

      let idx = 2;
      if (headerFile) {
        const files: OptionFileAttachment[] = results[idx++];
        setPreviewHeader(files[0]?.url || null);
        setHeaderFile(null);
      }
      if (footerFile) {
        const files: OptionFileAttachment[] = results[idx++];
        setPreviewFooter(files[0]?.url || null);
        setFooterFile(null);
      }

      showToast("All settings saved successfully", true);
    } catch (err: any) {
      console.error('❌ Save error:', err);
      showToast(err.message || "Failed to save", false);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setConfig(DEFAULTS);
    showToast("Block positions reset to defaults", true);
  };

  // ── render draggable block ────────────────────────────────────────────────
  const renderBlock = (key: BlockKey, label: string) => {
    const blk      = config[key];
    const style    = BLOCK_STYLES[key];
    const isActive = activeBlock === key;
    return (
      <div
        key={key}
        onMouseDown={(e) => onMouseDown(e, key)}
        onTouchStart={(e) => onTouchStart(e, key)}
        style={{
          position:     "absolute",
          left:         Math.round(blk.x * SCALE),
          top:          Math.round(blk.y * SCALE),
          width:        Math.round(blk.width * SCALE),
          height:       20,
          background:   isActive ? "rgba(40,85,174,0.18)" : style.bg,
          border:       `1.5px ${isActive ? "solid" : "dashed"} ${style.border}`,
          borderRadius: 3,
          cursor:       "grab",
          display:      "flex",
          alignItems:   "center",
          paddingLeft:  5,
          userSelect:   "none",
          zIndex:       10,
          boxShadow:    isActive ? "0 0 0 2px rgba(40,85,174,0.15)" : "none",
          transition:   "background 0.12s, box-shadow 0.12s",
        }}
      >
        <span style={{
          fontSize:     8,
          fontWeight:   700,
          color:        style.text,
          letterSpacing: 0.2,
          whiteSpace:   "nowrap",
          overflow:     "hidden",
          textOverflow: "ellipsis",
        }}>
          {label}
        </span>
      </div>
    );
  };

  if (!open) return null;

  const headerCanvasSrc = headerLocalPreview || previewHeader;
  const footerCanvasSrc = footerLocalPreview || previewFooter;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position:       "fixed",
          inset:          0,
          background:     "rgba(15,23,42,0.35)",
          zIndex:         1000,
          backdropFilter: "blur(3px)",
        }}
      />

      {/* Panel */}
      <div style={{
        position:      "fixed",
        top:           0,
        right:         0,
        bottom:        0,
        width:         460,
        background:    "#ffffff",
        zIndex:        1001,
        display:       "flex",
        flexDirection: "column",
        boxShadow:     "-8px 0 40px rgba(0,0,0,0.12)",
        animation:     "tspSlideIn 0.26s cubic-bezier(0.22,1,0.36,1)",
      }}>
        <style>{`
          @keyframes tspSlideIn {
            from { transform: translateX(100%); }
            to   { transform: translateX(0); }
          }
          @keyframes tspFadeUp {
            from { opacity:0; transform:translateX(-50%) translateY(6px); }
            to   { opacity:1; transform:translateX(-50%) translateY(0); }
          }
          .tsp-num-input::-webkit-inner-spin-button,
          .tsp-num-input::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
          .tsp-num-input { -moz-appearance: textfield; }
          .tsp-url-input:focus { outline: none; border-color: #2855ae !important; box-shadow: 0 0 0 3px rgba(40,85,174,0.10); }
          .tsp-num-input:focus  { outline: none; border-color: #2855ae !important; }
        `}</style>

        {/* ── Header bar ───────────────────────────────────────────────────── */}
        <div style={{
          background:     "linear-gradient(135deg, #2855ae 0%, #3b82f6 100%)",
          padding:        "18px 22px",
          display:        "flex",
          alignItems:     "center",
          justifyContent: "space-between",
          flexShrink:     0,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ background: "rgba(255,255,255,0.18)", borderRadius: 10, padding: "8px 10px" }}>
              <i className="pi pi-sliders-h" style={{ fontSize: 15, color: "white" }} />
            </div>
            <div>
              <h2 style={{ color: "white", fontSize: 16, fontWeight: 700, margin: 0, letterSpacing: -0.2 }}>
                Template Setup
              </h2>
              <p style={{ color: "rgba(255,255,255,0.65)", fontSize: 11.5, margin: 0 }}>
                Letterhead images &amp; field positions
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background:   "rgba(255,255,255,0.15)",
              border:       "none",
              borderRadius: 8,
              width:        30,
              height:       30,
              cursor:       "pointer",
              color:        "white",
              fontSize:     14,
              display:      "flex",
              alignItems:   "center",
              justifyContent: "center",
            }}
          >
            <i className="pi pi-times" />
          </button>
        </div>

        {/* ── Scrollable body ───────────────────────────────────────────────── */}
        <div style={{
          flex:          1,
          overflowY:     "auto",
          padding:       "20px 22px",
          display:       "flex",
          flexDirection: "column",
          gap:           22,
        }}>
          {loading ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, gap: 10, color: "#6b7280" }}>
              <i className="pi pi-spin pi-spinner" style={{ fontSize: 20, color: "#2855ae" }} />
              <span style={{ fontSize: 14 }}>Loading saved settings…</span>
            </div>
          ) : (
            <>
              {/* ── Letterhead Images ──────────────────────────────────────── */}
              <div>
                <SectionLabel icon="pi-image" title="Letterhead Images" />
                <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 12 }}>

                  <ImageUploadField
                    label="Header Image"
                    preview={headerLocalPreview || previewHeader}
                    fileName={headerFile?.name}
                    onPick={() => headerInputRef.current?.click()}
                    onRemove={handleRemoveHeader}
                  />
                  <input
                    ref={headerInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={onHeaderFilePicked}
                  />

                  <ImageUploadField
                    label="Footer Image"
                    preview={footerLocalPreview || previewFooter}
                    fileName={footerFile?.name}
                    onPick={() => footerInputRef.current?.click()}
                    onRemove={handleRemoveFooter}
                  />
                  <input
                    ref={footerInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={onFooterFilePicked}
                  />

                  <p style={{ fontSize: 11.5, color: "#9ca3af", margin: 0, lineHeight: 1.6 }}>
                    Saved as{" "}
                    <code style={{ background: "#f1f5f9", borderRadius: 4, padding: "1px 5px", fontSize: 11, color: "#475569" }}>
                      letterhead_header_image
                    </code>{" "}and{" "}
                    <code style={{ background: "#f1f5f9", borderRadius: 4, padding: "1px 5px", fontSize: 11, color: "#475569" }}>
                      letterhead_footer_image
                    </code>
                  </p>
                </div>
              </div>

              {/* Divider */}
              <div style={{ height: 1, background: "#f1f5f9" }} />

              {/* ── Field Positions ────────────────────────────────────────── */}
              <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <SectionLabel icon="pi-arrows-alt" title="Field Positions" />
                  <span style={{ fontSize: 11, color: "#9ca3af", display: "flex", alignItems: "center", gap: 4 }}>
                    <i className="pi pi-info-circle" style={{ fontSize: 11 }} />
                    Drag on canvas
                  </span>
                </div>

                {/* Legend */}
                <div style={{ display: "flex", gap: 8, marginTop: 12, marginBottom: 14 }}>
                  {BLOCK_META.map((b) => (
                    <div key={b.key} style={{
                      display:    "flex",
                      alignItems: "center",
                      gap:        6,
                      background: "#f8fafc",
                      border:     `1.5px dashed ${BLOCK_STYLES[b.key].border}`,
                      borderRadius: 8,
                      padding:    "5px 12px",
                    }}>
                      <i className={`pi ${b.icon}`} style={{ fontSize: 11, color: BLOCK_STYLES[b.key].text }} />
                      <span style={{ fontSize: 12, fontWeight: 600, color: BLOCK_STYLES[b.key].text }}>{b.label}</span>
                    </div>
                  ))}
                </div>

                {/* Mini A4 canvas */}
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <div style={{
                    position:     "relative",
                    width:        PANEL_CANVAS_W,
                    height:       PANEL_CANVAS_H,
                    background:   "white",
                    border:       "1px solid #e2e8f0",
                    borderRadius: 6,
                    overflow:     "hidden",
                    boxShadow:    "0 2px 16px rgba(0,0,0,0.08)",
                    flexShrink:   0,
                  }}>
                    {headerCanvasSrc ? (
                      <img
                        src={headerCanvasSrc} alt="header" crossOrigin="anonymous"
                        style={{ position: "absolute", top: 0, left: 0, width: "100%", zIndex: 1, display: "block" }}
                        onError={() => setPreviewHeader(null)}
                      />
                    ) : (
                      <div style={{
                        position: "absolute", top: 0, left: 0, width: "100%",
                        height: Math.round(160 * SCALE),
                        background: "linear-gradient(135deg,#1e3a8a,#3b82f6)", zIndex: 1,
                      }} />
                    )}

                    {footerCanvasSrc ? (
                      <img
                        src={footerCanvasSrc} alt="footer" crossOrigin="anonymous"
                        style={{ position: "absolute", bottom: 0, left: 0, width: "100%", zIndex: 1, display: "block" }}
                        onError={() => setPreviewFooter(null)}
                      />
                    ) : (
                      <div style={{
                        position: "absolute", bottom: 0, left: 0, width: "100%",
                        height: Math.round(110 * SCALE),
                        background: "linear-gradient(135deg,#1e3a8a,#3b82f6)", zIndex: 1,
                      }} />
                    )}

                    {[0.25, 0.5, 0.75].map((f) => (
                      <div key={f} style={{
                        position: "absolute", left: "4%", right: "4%",
                        top: Math.round(PAGE_H * f * SCALE),
                        height: 1, background: "#f1f5f9", zIndex: 2,
                      }} />
                    ))}

                    {BLOCK_META.map((b) => renderBlock(b.key, b.label))}
                  </div>
                </div>

                {/* Fine-tune inputs */}
                <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 16 }}>
                  {BLOCK_META.map((b) => (
                    <div key={b.key} style={{
                      background: "#f8fafc", borderRadius: 10,
                      padding: "12px 14px", border: "1px solid #e5e7eb",
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 10 }}>
                        <i className={`pi ${b.icon}`} style={{ fontSize: 12, color: BLOCK_STYLES[b.key].text }} />
                        <span style={{ fontSize: 12.5, fontWeight: 700, color: "#1f2937" }}>{b.label}</span>
                        <span style={{ fontSize: 10.5, color: "#9ca3af", marginLeft: "auto" }}>px · A4 canvas</span>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                        {(["x", "y", "width"] as const).map((field) => (
                          <div key={field}>
                            <label style={{
                              display: "block", fontSize: 10, fontWeight: 600,
                              color: "#6b7280", textTransform: "uppercase",
                              letterSpacing: 0.5, marginBottom: 4,
                            }}>
                              {field === "width" ? "W" : field.toUpperCase()}
                            </label>
                            <input
                              type="number"
                              className="tsp-num-input"
                              value={config[b.key][field]}
                              onChange={(e) => nudge(b.key, field, Number(e.target.value))}
                              style={{
                                width:        "100%",
                                boxSizing:    "border-box",
                                border:       "1.5px solid #e5e7eb",
                                borderRadius: 7,
                                padding:      "6px 8px",
                                fontSize:     13,
                                fontWeight:   600,
                                color:        "#1f2937",
                                background:   "white",
                                textAlign:    "center",
                                transition:   "border-color 0.15s",
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <p style={{ fontSize: 11.5, color: "#9ca3af", margin: "12px 0 0 0", lineHeight: 1.6 }}>
                  Coordinates saved as{" "}
                  <code style={{ background: "#f1f5f9", borderRadius: 4, padding: "1px 5px", fontSize: 11, color: "#475569" }}>
                    letterhead_serial
                  </code>{" "}and{" "}
                  <code style={{ background: "#f1f5f9", borderRadius: 4, padding: "1px 5px", fontSize: 11, color: "#475569" }}>
                    letterhead_date
                  </code>
                </p>
              </div>
            </>
          )}
        </div>

        {/* ── Footer actions ────────────────────────────────────────────────── */}
        <div style={{
          padding:    "14px 22px",
          borderTop:  "1px solid #f1f5f9",
          display:    "flex",
          gap:        8,
          flexShrink: 0,
          background: "white",
        }}>
          <button
            onClick={handleReset}
            title="Reset block positions to defaults"
            style={{
              background: "transparent", border: "1.5px solid #e5e7eb",
              borderRadius: 9, padding: "8px 14px", cursor: "pointer",
              color: "#6b7280", fontSize: 13, fontWeight: 600,
              display: "flex", alignItems: "center", gap: 6,
              transition: "all 0.15s",
            }}
            onMouseOver={(e) => { e.currentTarget.style.borderColor = "#2855ae"; e.currentTarget.style.color = "#2855ae"; }}
            onMouseOut={(e)  => { e.currentTarget.style.borderColor = "#e5e7eb"; e.currentTarget.style.color = "#6b7280"; }}
          >
            <i className="pi pi-refresh" style={{ fontSize: 12 }} />
            Reset
          </button>

          <button
            onClick={onClose}
            style={{
              background: "transparent", border: "1.5px solid #e5e7eb",
              borderRadius: 9, padding: "8px 14px", cursor: "pointer",
              color: "#6b7280", fontSize: 13, fontWeight: 600,
              transition: "all 0.15s",
            }}
            onMouseOver={(e) => { e.currentTarget.style.borderColor = "#d1d5db"; e.currentTarget.style.color = "#374151"; }}
            onMouseOut={(e)  => { e.currentTarget.style.borderColor = "#e5e7eb"; e.currentTarget.style.color = "#6b7280"; }}
          >
            Cancel
          </button>

          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              flex:           1,
              background:     saving ? "#93c5fd" : "linear-gradient(135deg, #2855ae 0%, #3b82f6 100%)",
              border:         "none",
              borderRadius:   9,
              padding:        "8px 20px",
              cursor:         saving ? "not-allowed" : "pointer",
              color:          "white",
              fontSize:       13,
              fontWeight:     700,
              display:        "flex",
              alignItems:     "center",
              justifyContent: "center",
              gap:            7,
              opacity:        saving ? 0.75 : 1,
              transition:     "opacity 0.15s",
            }}
          >
            {saving
              ? <><i className="pi pi-spin pi-spinner" style={{ fontSize: 13 }} /> Saving…</>
              : <><i className="pi pi-save"            style={{ fontSize: 13 }} /> Save All</>
            }
          </button>
        </div>

        {/* ── Toast ─────────────────────────────────────────────────────────── */}
        {toast && (
          <div style={{
            position:     "absolute",
            bottom:       78,
            left:         "50%",
            transform:    "translateX(-50%)",
            background:   toast.ok ? "#1e3a8a" : "#ef4444",
            color:        "white",
            borderRadius: 10,
            padding:      "9px 18px",
            fontSize:     12.5,
            fontWeight:   600,
            whiteSpace:   "nowrap",
            boxShadow:    "0 4px 20px rgba(0,0,0,0.18)",
            animation:    "tspFadeUp 0.2s ease",
            zIndex:       20,
            display:      "flex",
            alignItems:   "center",
            gap:          7,
          }}>
            <i className={`pi ${toast.ok ? "pi-check-circle" : "pi-times-circle"}`} style={{ fontSize: 13 }} />
            {toast.msg}
          </div>
        )}
      </div>
    </>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function SectionLabel({ icon, title }: { icon: string; title: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{
        background:   "linear-gradient(135deg, #2855ae, #3b82f6)",
        borderRadius: 7,
        width:        26,
        height:       26,
        display:      "flex",
        alignItems:   "center",
        justifyContent: "center",
        flexShrink:   0,
      }}>
        <i className={`pi ${icon}`} style={{ fontSize: 12, color: "white" }} />
      </div>
      <span style={{ fontSize: 13.5, fontWeight: 700, color: "#1e293b", letterSpacing: -0.1 }}>{title}</span>
    </div>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label style={{
      display:       "block",
      fontSize:      11,
      fontWeight:    600,
      color:         "#6b7280",
      textTransform: "uppercase",
      letterSpacing: 0.5,
      marginBottom:  6,
    }}>
      {children}
    </label>
  );
}

function ImageUploadField({
  label,
  preview,
  fileName,
  onPick,
  onRemove,
}: {
  label: string;
  preview: string | null;
  fileName?: string;
  onPick: () => void;
  onRemove: () => void;
}) {
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <div style={{
        display:      "flex",
        alignItems:   "center",
        gap:          10,
        border:       "1.5px dashed #e5e7eb",
        borderRadius: 10,
        padding:      "10px 12px",
        background:   "#fafafa",
      }}>
        {preview ? (
          <img
            src={preview}
            alt={label}
            style={{ width: 56, height: 36, objectFit: "cover", borderRadius: 6, border: "1px solid #e5e7eb", flexShrink: 0 }}
          />
        ) : (
          <div style={{
            width: 56, height: 36, borderRadius: 6, background: "#eef2f7",
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <i className="pi pi-image" style={{ fontSize: 14, color: "#9ca3af" }} />
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 12.5, fontWeight: 600, color: "#374151",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {fileName || (preview ? "Current image" : "No image uploaded")}
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
            <button
              type="button"
              onClick={onPick}
              style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 12, fontWeight: 700, color: "#2855ae" }}
            >
              {preview ? "Change" : "Upload"}
            </button>
            {preview && (
              <button
                type="button"
                onClick={onRemove}
                style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 12, fontWeight: 700, color: "#ef4444" }}
              >
                Remove
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}